import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { useReviews } from "hooks/LabelPropagation/useGetReview";
import { useReviewCounters } from "hooks/LabelPropagation/useReviewCounters";
import { CLUSTER_DETAILS_PAGE, DATA_PAGE } from "helpers/constants/pages";
import DataList from "../DataList";
import {
  FLOW_PARAMETER,
  LABEL_CLASS_PARAMETER,
  REVIEW_TAB_PARAMETER,
} from "helpers/constants/miscellaneous";
import ClusterDetailsFilter from "../DataPageFilters/ClusterDetailsFilter";
import { Option } from "views/uikit/FormFields/SelectList";
import { useDispatch, useSelector } from "react-redux";
import {
  getNavigationCluster,
  getTemporaryExportDataArray,
} from "redux/SingleDataset/selectors";
import { setTemporaryExportData } from "redux/SingleDataset/actions";
import ReviewModal from "views/components/LabelPropagation/ReviewModal";
import styles from "../DataList/style.module.scss";
import { VLSegmented } from "views/uikit/VLSegmented/VLSegmented";
import { NoMatchFoundCardProps } from "views/components/NoMatchFoundCard";
import ReviewCounters from "views/components/LabelPropagation/ReviewCounters/ReviewCounters";
import { useSearchParams } from "react-router-dom";

interface LabelPropagationProps {
  onLabelClick: (value: string[]) => void;
  options?: Option[];
  displayNewFloatingPanel?: boolean;
  showLabelPropagation: boolean;
}

interface ReviewListProps {
  onReviewAction: (sampleId?: string) => void;
  setReviewCounters: (text: React.ReactElement | null) => void;
  onNavigateToCluster: () => void;
  updateNavigationCluster: (data: any) => void;
  labelPropagation: LabelPropagationProps;
  reviewCounters: ReactElement | null;
}

export type FilterTab = "All" | "For Review" | "Annotated";

const SEGMENTED_OPTIONS = ["All", "For Review", "Annotated"];

export default function ReviewList({
  onReviewAction,
  setReviewCounters,
  onNavigateToCluster,
  labelPropagation,
  updateNavigationCluster,
  reviewCounters,
}: ReviewListProps) {
  const dispatch = useDispatch();
  const { datasetId, clusterId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedFilterTab = searchParams.get(REVIEW_TAB_PARAMETER) as FilterTab;
  const setSelectedFilterTab = useCallback(
    (tab: FilterTab) => {
      searchParams.set(REVIEW_TAB_PARAMETER, tab);
      setSearchParams(searchParams);
    },
    [searchParams, setSearchParams],
  );
  const location = useLocation();
  const navigate = useNavigate();
  const [reviewModalOpen, setReviewModalOpen] = useState<string>();
  const filterItemsFn = useMemo<(item: any) => boolean>(() => {
    switch (selectedFilterTab) {
      case "For Review":
        return (item: any) => !item?.completed;
      case "Annotated":
        return (item: any) => item?.completed;
      default:
        return () => true;
    }
  }, [selectedFilterTab]);

  const removeLabel = useCallback(() => {
    const params = new URLSearchParams(location.search);
    params.delete(LABEL_CLASS_PARAMETER);
    params.delete(REVIEW_TAB_PARAMETER);
    navigate({ search: `?${params.toString()}` });
  }, [navigate, location.search]);

  const flowId = searchParams.get(FLOW_PARAMETER);
  const labelId = searchParams.get(LABEL_CLASS_PARAMETER);
  const exportData = useSelector(getTemporaryExportDataArray);
  const navigationCluster = useSelector(getNavigationCluster);
  const clearSelectedMedia = () => dispatch(setTemporaryExportData(null));

  const {
    reviews,
    selectedReview,
    setSelectedReview,
    loading,
    bulkActionList,
    allLabelsOptions,
    updateReview,
    totalReviewsCount,
    pendingReviewsCount,
  } = useReviews(
    onReviewAction,
    setReviewModalOpen,
    datasetId,
    flowId,
    labelId,
    exportData,
    clearSelectedMedia,
  );

  const emptyStateProps = useMemo<NoMatchFoundCardProps>(() => {
    const noActionsState = {
      onActionClick: () => removeLabel(),
      buttonText: "Back to Clusters",
      text: "No action required for this class.",
    };
    if (selectedReview && !selectedReview.previews.length) {
      return noActionsState;
    }
    switch (selectedFilterTab) {
      case "All":
        return noActionsState;
      case "For Review":
        return {
          onActionClick: () => removeLabel(),
          buttonText: "Back to Clusters",
          text: "All images under this class were already reviewed in this iteration.",
        };
      case "Annotated":
        return {
          onActionClick: () => setSelectedFilterTab("For Review"),
          buttonText: "Back to Review",
          text: "There are no annotated results for this class yet.",
        };
    }
  }, [removeLabel, selectedFilterTab, selectedReview, setSelectedFilterTab]);

  const clusterLabel = useMemo(() => {
    if (!selectedReview) return undefined;

    return {
      text: selectedReview.label.name,
      onClick: () => {
        const params = new URLSearchParams(location.search);
        params.delete(LABEL_CLASS_PARAMETER);
        params.delete(REVIEW_TAB_PARAMETER);
        navigate({ search: `?${params.toString()}` });
        setSelectedReview(null);
        setReviewCounters(null);
      },
      tooltip: "Click to go back",
    };
  }, [
    selectedReview,
    location.search,
    navigate,
    setSelectedReview,
    setReviewCounters,
  ]);

  const showClusterFilter = (!!clusterId || !!labelId) && !!datasetId;
  useEffect(() => {
    onNavigateToCluster();
  }, [labelId, onNavigateToCluster]);

  useEffect(() => {
    if (selectedReview) {
      updateNavigationCluster({
        ...selectedReview,
        previews: selectedReview.previews.filter(filterItemsFn),
      });
    }
  }, [filterItemsFn, selectedReview, updateNavigationCluster]);

  const limitedPreviewReviews = (reviews || []).map((review) => ({
    ...review,
    previews: review.previews?.slice(0, 9) ?? [],
  }));

  // Use centralized counter calculation hook
  const counters = useReviewCounters({
    selectedReview,
    labelId,
    selectedFilterTab,
    totalReviewsCount,
    pendingReviewsCount,
    loading,
  });

  useEffect(() => {
    if (counters) {
      setReviewCounters(<ReviewCounters counters={counters} />);
    } else {
      setReviewCounters(null);
    }
  }, [counters, setReviewCounters]);

  const displayedPreviews = useMemo(
    () => selectedReview?.previews?.filter(filterItemsFn) || [],
    [selectedReview, filterItemsFn],
  );

  useEffect(() => {
    if (!displayedPreviews.length) {
      setReviewModalOpen(undefined);
    }
  }, [displayedPreviews]);

  const selectedItem = displayedPreviews.findIndex(
    (r) => r.media_id === reviewModalOpen,
  );

  const openZoomInModal =
    !!reviewModalOpen && !!selectedReview && displayedPreviews.length > 0;
  return (
    <>
      <DataList
        page={labelId ? CLUSTER_DETAILS_PAGE : DATA_PAGE}
        clusters={limitedPreviewReviews || []}
        navigationCluster={labelId ? navigationCluster : undefined}
        isContentLoading={loading}
        showMetadataSummary={true}
        labelPropagation={labelPropagation}
        isDataPage={!!datasetId && !labelId}
        rightPanelMode="labels"
        reviewCounters={reviewCounters}
        title={
          <div className={styles.title}>
            <span className={styles.titleText}>Review</span>
            {showClusterFilter && <ClusterDetailsFilter label={clusterLabel} />}
            <div style={{ flexGrow: 1 }} />
            {labelId && (
              <VLSegmented
                options={SEGMENTED_OPTIONS}
                value={selectedFilterTab}
                onChange={(tab) => setSelectedFilterTab(tab as FilterTab)}
              />
            )}
          </div>
        }
        bulkActionList={bulkActionList}
        emptyStateProps={emptyStateProps}
      />
      {reviews && reviewModalOpen && selectedReview && (
        <ReviewModal
          selectedLabel={selectedReview?.label.name}
          initialSelectedItem={selectedItem}
          open={openZoomInModal}
          onClose={() => setReviewModalOpen(undefined)}
          labelsOptions={allLabelsOptions}
          imagesList={displayedPreviews.map((p) => ({
            src: p.media_uri ?? "",
            title: p.title ?? "",
            previewBorderColor: p.borderColor,
            floatingDetails: p.floatingDetails,
            previewFloatingDetails:
              p.floatingDetails?.type === "default"
                ? p.floatingDetails
                : undefined,
            opacity: p.opacity ?? 1,
            actionList: p.modalActionList,
            labelId: p.labelId,
            id: p.media_id,
            borderColor: p.completed
              ? undefined
              : "var(--new-primary-orange-500)",
          }))}
          onCheckBoxClick={async (e) => {
            if (e.labelId === labelId) {
              await updateReview(e.sampleId, "CORRECT");
            } else {
              await updateReview(e.sampleId, "REASSIGN", e.labelId);
            }
          }}
          counters={counters || undefined}
        />
      )}
    </>
  );
}
